// Journal entry detail: one balanced posting, read-only, with an audit
// timeline. The create endpoint prepares a reversing DRAFT, not a posted
// correction. Eligible manual drafts have a separate reviewed posting action.

import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { PeriodLockWarning } from "@/components/PeriodLockWarning";
import { journalSearch } from "./journal-search";
import { ManualJournalPosting } from "./ManualJournalPosting";
import { useAccountLabels } from "@/lib/labels";
import {
  type AuditEvent,
  type CreateJournalEntryInput,
  createJournalEntry,
  getJournalEntry,
  getPeriodLock,
  type JournalEntry,
  listAuditEvents,
} from "@/lib/api";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "posted":
      return "success";
    case "void":
      return "danger";
    default:
      return "idle";
  }
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

export function JournalEntryDetailPage() {
  const { entryId } = useParams({ strict: false }) as { entryId: string };
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [lockDate, setLockDate] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [reverseOpen, setReverseOpen] = useState(false);
  const accountLabels = useAccountLabels();

  const reloadEvents = useCallback(() => {
    if (!entryId) return;
    setEvents(null);
    setEventsError(null);
    listAuditEvents({ resource_id: entryId })
      .then(setEvents)
      .catch((cause: unknown) => setEventsError(toErrorMessage(cause)));
  }, [entryId]);

  const reload = useCallback(() => {
    if (!entryId) return;
    setError(null);
    getJournalEntry(entryId)
      .then(setEntry)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
    reloadEvents();
    getPeriodLock()
      .then((lock) => setLockDate([lock.lock_date, lock.advisor_lock_date].filter((date): date is string => !!date).sort().at(-1)))
      .catch(() => setLockDate(undefined));
  }, [entryId, reloadEvents]);

  useEffect(reload, [reload]);

  const balanced =
    entry !== null && entry.total_debit_cents === entry.total_credit_cents;

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/journal"
          search={previous => journalSearch(previous)}
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          General ledger
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {entry?.entry_number ?? entryId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {entry === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : entry ? (
        <>
          <PageHeader
            title={entry.entry_number}
            description={
              <>
                {typeof entry.entry_date === "string"
                  ? entry.entry_date.slice(0, 10)
                  : String(entry.entry_date)}{" "}
                · {entry.memo} ·{" "}
                <StatusDot
                  tone={statusTone(entry.status)}
                  label={entry.status}
                />
              </>
            }
            actions={entry.status === "posted" ?
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setReverseOpen(true)}
                title="Prepare an unposted draft with mirrored lines; the original remains unchanged"
              >
                Prepare reversal
              </Button>
            : undefined}
          />

          {entry.status === "draft" ? <p role="status" className="text-sm text-[var(--muted-foreground)]">This draft is not posted to the ledger. It cannot be corrected by posting a reversal.</p> : null}
          <ManualJournalPosting key={entry.id} entry={entry} lockDate={lockDate} onEntry={updated => { setEntry(updated); reloadEvents(); }} />

          <div className="surface overflow-hidden rounded-2xl">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entry.lines.map((line, index) => (
                  <TableRow key={`${entry.id}-line-${index}`}>
                    <TableCell className="text-xs">
                      {accountLabels.has(line.account_id) ? (
                        accountLabels.get(line.account_id)
                      ) : (
                        <span
                          className="text-[var(--muted-foreground)]"
                          title={line.account_id}
                        >
                          Unknown account
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {line.debit_cents > 0 ? (
                        formatMoney({
                          cents: line.debit_cents,
                          currency: "USD",
                        })
                      ) : (
                        <span className="text-[var(--muted-foreground)]">
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {line.credit_cents > 0 ? (
                        formatMoney({
                          cents: line.credit_cents,
                          currency: "USD",
                        })
                      ) : (
                        <span className="text-[var(--muted-foreground)]">
                          —
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell className="font-medium">Totals</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {formatMoney({
                      cents: entry.total_debit_cents,
                      currency: "USD",
                    })}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {formatMoney({
                      cents: entry.total_credit_cents,
                      currency: "USD",
                    })}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p
            className="text-xs text-[var(--muted-foreground)]"
            title="Every entry balances: total debits equal total credits"
          >
            {balanced
              ? "This entry balances — total debits equal total credits."
              : "Warning: this entry is out of balance — total debits do not equal total credits."}
          </p>

          <section aria-label="Entry history" className="grid gap-3">
            <h2 className="text-sm font-semibold">History</h2>
            {eventsError ? (
              <div
                role="alert"
                className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
              >
                Could not load entry history: {eventsError}
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={reloadEvents}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            ) : (
              <div className="surface overflow-hidden rounded-2xl">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>What happened</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events === null ? (
                      <TableRow>
                        <TableCell colSpan={2}>Loading…</TableCell>
                      </TableRow>
                    ) : events.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          No recorded activity for this entry yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      events.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell className="whitespace-nowrap text-xs">
                            {new Date(event.occurred_at).toLocaleString()}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {event.action}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </>
      ) : null}

      {reverseOpen && entry ? (
        <ReverseEntryDrawer
          entry={entry}
          lockDate={lockDate}
          onClose={() => setReverseOpen(false)}
          onDone={reload}
        />
      ) : null}
    </div>
  );
}

function ReverseEntryDrawer({
  entry,
  lockDate,
  onClose,
  onDone,
}: {
  entry: JournalEntry;
  lockDate?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reverseDate, setReverseDate] = useState(todayInput);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<CreateJournalEntryInput | null>(null);
  const [createdDraft, setCreatedDraft] = useState<JournalEntry | null>(null);

  // Validate the date, then stage the payload for the confirmation Dialog.
  // The API call happens only after the user confirms there.
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    try {
      if (!reverseDate) throw new Error("Reversal date is required.");
      setPending({
        entry_date: reverseDate,
        memo: `Reversal of ${entry.entry_number} — ${entry.memo}`,
        lines: entry.lines.map((line) => ({
          account_id: line.account_id,
          // Mirror the original: what went in now goes out, and vice versa.
          debit_cents: line.credit_cents,
          credit_cents: line.debit_cents,
          ...(line.location_id ? { location_id: line.location_id } : {}),
          ...(line.area_id ? { area_id: line.area_id } : {}),
          ...(line.counterparty_legal_entity_id
            ? { counterparty_legal_entity_id: line.counterparty_legal_entity_id }
            : {}),
        })),
      });
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  const createDraft = async () => {
    if (!pending) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createJournalEntry(pending);
      if (!created.id || created.status !== "draft") {
        throw new Error("Draft creation was not confirmed. Check the ledger before retrying.");
      }
      setCreatedDraft(created);
      setPending(null);
      onDone();
    } catch (cause) {
      setPending(null);
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const originalDate =
    typeof entry.entry_date === "string"
      ? entry.entry_date.slice(0, 10)
      : String(entry.entry_date);

  return (
    <>
      <Drawer
        title={`Prepare reversal of ${entry.entry_number}`}
        description="Creates an unposted draft with mirrored debits and credits. The original entry and posted balances stay unchanged."
        open
        onOpenChange={(next) => {
          if (!next && !saving) onClose();
        }}
      >
        {createdDraft ? (
          <div role="status" className="grid gap-3 text-sm">
            <p>Reversing draft created. The original has not been reversed; this draft is not posted.</p>
            <Link to="/journal/$entryId" params={{ entryId: createdDraft.id }} search={previous => journalSearch(previous)} onClick={onClose} className="text-[var(--primary)] underline">View reversing draft</Link>
            <Button type="button" variant="secondary" onClick={onClose}>Done</Button>
          </div>
        ) : <form onSubmit={submit} className="grid gap-3">
          <PeriodLockWarning lockDate={lockDate} chosenDate={reverseDate} />
          {error ? (
            <p role="alert" className="text-sm text-[var(--danger)]">
              {error}
            </p>
          ) : null}
          <label
            htmlFor="reverse-date"
            className="grid gap-1 text-xs font-medium"
          >
            Reversal date
            <Input
              id="reverse-date"
              type="date"
              value={reverseDate}
              onChange={(e) => setReverseDate(e.target.value)}
              required
              disabled={saving}
            />
          </label>
          <p className="text-xs text-[var(--muted-foreground)]">
            Prepares {entry.lines.length}{" "}
            {entry.lines.length === 1 ? "line" : "lines"} totalling{" "}
            {formatMoney({ cents: entry.total_debit_cents, currency: "USD" })}{" "}
            each way.
          </p>
          <Button type="submit" disabled={saving}>
            Create reversing draft
          </Button>
        </form>}
      </Drawer>

      <Dialog
        open={pending !== null}
        onOpenChange={(next) => {
          if (!next && !saving) setPending(null);
        }}
        title="Create this reversing draft?"
        description={
          pending
            ? `Creates a draft dated ${pending.entry_date} with mirrored lines from ${entry.entry_number} dated ${originalDate}. This does not reverse the original or change posted balances.`
            : undefined
        }
      >
        {pending ? (
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPending(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={createDraft} disabled={saving}>
              {saving ? "Creating…" : "Create reversing draft"}
            </Button>
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
